import {
  FC,
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { PrevisitInsightsStatus } from '@eluve/api-contract';
import {
  Heading,
  Icon,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@eluve/components';
import { SupportedVendors } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import {
  EluveRenderer,
  SDUIAnalyticsProvider,
  SDUIFeedbackProvider,
} from '@eluve/sdui-renderer';
import { type Spec, getRootChildKeys } from '@eluve/sdui-types';

import { CustomChartsInput } from './CustomChartsInput';
import { LoadingInsights } from './LoadingInsights';
import { AI_INSIGHTS_TOOLTIP_KEY, INSIGHTS_TITLE_KEY } from './constants';
import {
  InsightsAnalyticsState,
  type InsightsPanelView,
  useInsightsPanelAnalytics,
} from './useInsightsPanelAnalytics';

export const INSIGHTS_PANEL_BOTTOM_MARKER_TEST_ID =
  'insights-panel-bottom-marker';

/** Renders the resolved insights content. */
const InsightsContent: FC<{
  spec: Spec;
  bottomMarkerRef: React.Ref<HTMLDivElement>;
  status?: PrevisitInsightsStatus;
}> = ({ spec, bottomMarkerRef, status }) => {
  return (
    <Stack gap={3} align="stretch" className="posthog-privacy-text flex-1">
      <EluveRenderer spec={spec} />
      {status === 'success' && (
        <div
          ref={bottomMarkerRef}
          data-testid={INSIGHTS_PANEL_BOTTOM_MARKER_TEST_ID}
        />
      )}
    </Stack>
  );
};

export interface CustomChartsModeConfig {
  enabled: boolean;
  isLoadingConfig: boolean;
  onSubmitCharts: (charts: Record<string, unknown>[]) => Promise<void>;
  vendor?: SupportedVendors;
}

export interface InsightsPanelProps {
  showCard?: boolean;
  hideTitle?: boolean;
  spec: Spec | null;
  /** Backend-derived status for the previsit insights artifact */
  status?: PrevisitInsightsStatus;
  isLoading: boolean;
  /** Timestamp when loading started, used for accurate progress display */
  loadingStartedAt?: number | null;
  error?: string | null;
  customChartsMode?: CustomChartsModeConfig;
  appointmentId: string;
  /** Render feedback UI for each expandable insight section; receives a generic metadata object */
  renderFeedback?: (metadata: Record<string, unknown>) => ReactNode;
  /** Current view derived from appointment status for analytics */
  view?: InsightsPanelView;
  /** Optional actions rendered to the right of the title row (e.g., a regenerate button) */
  headerActions?: ReactNode;
}

export type InsightsPanelHandle = {
  getAnalyticsState: () => InsightsAnalyticsState;
};

export const InsightsPanel = forwardRef<
  InsightsPanelHandle,
  InsightsPanelProps
>(
  (
    {
      showCard = false,
      hideTitle = false,
      spec,
      status,
      isLoading,
      loadingStartedAt,
      error,
      customChartsMode,
      appointmentId,
      renderFeedback,
      view,
      headerActions,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    // Track completing animation state for smooth transition
    const [isCompletingTransition, setIsCompletingTransition] = useState(false);
    const hasStartedLoadingRef = useRef(false);
    const hasCompletedTransitionRef = useRef(false);

    const specChildCount = getRootChildKeys(spec).length;

    // Track if loading has ever started
    useEffect(() => {
      if (isLoading) {
        hasStartedLoadingRef.current = true;
      }
    }, [isLoading]);

    // Detect when loading finishes and trigger completing animation
    useEffect(() => {
      // If loading just finished and we have content, trigger completing animation
      if (
        !isLoading &&
        hasStartedLoadingRef.current &&
        !hasCompletedTransitionRef.current &&
        specChildCount > 0
      ) {
        setIsCompletingTransition(true);
      }
    }, [isLoading, specChildCount]);

    const handleCompleteTransition = () => {
      setIsCompletingTransition(false);
      hasCompletedTransitionRef.current = true;
    };

    // Determine if we're in the gap between loading ending and transition starting
    // This prevents LoadingInsights from unmounting during that frame
    const isInTransitionGap =
      !isLoading &&
      hasStartedLoadingRef.current &&
      !hasCompletedTransitionRef.current &&
      !isCompletingTransition &&
      specChildCount > 0;

    // Show loading state during actual loading OR during completing transition OR gap
    const showLoadingState =
      isLoading || isCompletingTransition || isInTransitionGap;

    const isContentRendered = !showLoadingState && specChildCount > 0;

    const {
      containerRef,
      bottomMarkerRef,
      incrementSectionsExpanded,
      addExpandedTime,
      registerExpandedSection,
      incrementLinkClicks,
      getTimeSinceLoaded,
      getAnalyticsState,
    } = useInsightsPanelAnalytics({
      appointmentId,
      spec,
      error,
      status,
      isContentRendered,
      view,
    });

    useImperativeHandle(ref, () => ({
      getAnalyticsState,
    }));

    if (customChartsMode?.isLoadingConfig) {
      return null;
    }

    if (customChartsMode?.enabled && !spec && !isLoading) {
      return (
        <CustomChartsInput
          onSubmitCharts={customChartsMode.onSubmitCharts}
          isGenerating={isLoading}
          vendor={customChartsMode.vendor}
        />
      );
    }

    if (!isLoading && specChildCount === 0) {
      return null;
    }

    return (
      <SDUIAnalyticsProvider
        value={{
          appointmentId,
          view,
          getTimeSinceLoaded,
          onSectionExpanded: incrementSectionsExpanded,
          onSectionCollapsed: addExpandedTime,
          registerExpandedSection,
          onLinkClicked: incrementLinkClicks,
        }}
      >
        <SDUIFeedbackProvider value={{ renderFeedback }}>
          <Stack
            ref={containerRef}
            align="stretch"
            className={cn(
              'flex-1',
              showCard &&
                'rounded-xl border border-border-primary bg-white p-4',
            )}
          >
            <Stack gap={4} align="stretch" className="flex-1">
              {!hideTitle && (
                <Stack
                  align="center"
                  justify="between"
                  gap={2}
                  direction="horizontal"
                >
                  <Stack align="center" gap={2} direction="horizontal">
                    <Heading color="secondary" size="lg" weight="semibold">
                      {t(INSIGHTS_TITLE_KEY)}
                    </Heading>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="mt-0.5 cursor-help">
                          <Icon
                            name="CircleHelp"
                            size="sm"
                            className="text-text-tertiary"
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {t(AI_INSIGHTS_TOOLTIP_KEY)}
                      </TooltipContent>
                    </Tooltip>
                  </Stack>
                  {headerActions}
                </Stack>
              )}
              {showLoadingState ? (
                <LoadingInsights
                  loadingStartedAt={loadingStartedAt}
                  isCompleting={isCompletingTransition}
                  onComplete={handleCompleteTransition}
                />
              ) : specChildCount > 0 && spec ? (
                <InsightsContent
                  spec={spec}
                  bottomMarkerRef={bottomMarkerRef}
                  status={status}
                />
              ) : null}
            </Stack>
          </Stack>
        </SDUIFeedbackProvider>
      </SDUIAnalyticsProvider>
    );
  },
);
